import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FilePlus2,
  Users,
  LayoutTemplate,
  Settings as SettingsIcon,
  Award,
  Calendar,
  BookOpen,
  Palette,
  ArrowRight,
} from "lucide-react";
import { api } from "../lib/api";

const tiles = [
  {
    to: "/new",
    title: "New Certificate",
    desc: "Fill the form, generate a print-ready PDF in seconds.",
    icon: FilePlus2,
    accent: "from-blue-700 to-blue-500",
    testId: "tile-new",
  },
  {
    to: "/records",
    title: "Student Records",
    desc: "Search, reprint, or export the complete history.",
    icon: Users,
    accent: "from-slate-800 to-slate-600",
    testId: "tile-records",
  },
  {
    to: "/templates",
    title: "Certificate Templates",
    desc: "Design layouts, upload logos, tune fonts & colors.",
    icon: LayoutTemplate,
    accent: "from-indigo-700 to-indigo-500",
    testId: "tile-templates",
  },
  {
    to: "/settings",
    title: "Settings",
    desc: "Institute details, signature, backup & password.",
    icon: SettingsIcon,
    accent: "from-teal-700 to-teal-500",
    testId: "tile-settings",
  },
];

export default function Dashboard() {
  const [stats, setStats] = useState({
    total_certificates: 0,
    this_year: 0,
    courses: 0,
    templates: 0,
    recent: [],
  });

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <header className="mb-8">
        <p className="text-sm font-medium text-blue-800 uppercase tracking-wider">
          Dashboard
        </p>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mt-1">
          Welcome back, Admin.
        </h1>
        <p className="text-slate-500 mt-2 max-w-xl">
          Everything you need to issue and manage student certificates for
          Lovely Institute.
        </p>
      </header>

      {/* stats */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard
          icon={Award}
          label="Total certificates"
          value={stats.total_certificates}
          testId="stat-total"
        />
        <StatCard
          icon={Calendar}
          label={`Issued in ${new Date().getFullYear()}`}
          value={stats.this_year}
          testId="stat-year"
        />
        <StatCard
          icon={BookOpen}
          label="Courses"
          value={stats.courses}
          testId="stat-courses"
        />
        <StatCard
          icon={Palette}
          label="Templates"
          value={stats.templates}
          testId="stat-templates"
        />
      </section>

      {/* main tiles */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {tiles.map(({ to, title, desc, icon: Icon, accent, testId }) => (
          <Link
            key={to}
            to={to}
            data-testid={testId}
            className="group relative rounded-2xl border border-slate-200 bg-white p-6 hover:border-blue-300 hover:shadow-lg transition-soft"
          >
            <div
              className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} text-white grid place-items-center mb-4 group-hover:scale-105 transition-transform duration-200`}
            >
              <Icon size={22} />
            </div>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold text-slate-900">
                {title}
              </h3>
              <ArrowRight
                size={18}
                className="text-slate-400 group-hover:text-blue-700 group-hover:translate-x-1 transition-soft"
              />
            </div>
            <p className="text-slate-500 mt-1.5">{desc}</p>
          </Link>
        ))}
      </section>

      {/* recent */}
      {stats.recent && stats.recent.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-xl font-semibold text-slate-900">
              Recent certificates
            </h2>
            <Link
              to="/admin/records"
              className="text-sm text-blue-800 hover:text-blue-900 font-medium"
              data-testid="recent-view-all"
            >
              View all →
            </Link>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Certificate No.</th>
                  <th className="px-4 py-3 font-medium">Student</th>
                  <th className="px-4 py-3 font-medium">Course</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent.map((s) => (
                  <tr
                    key={s.id}
                    className="border-t border-slate-100 hover:bg-slate-50 transition-soft"
                  >
                    <td className="px-4 py-3 font-mono text-blue-800">
                      {s.certificate_number}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      {s.student_name}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{s.course_name}</td>
                    <td className="px-4 py-3 text-slate-500">{s.issue_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-slate-200 bg-white p-5"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">
          {label}
        </span>
        <Icon size={16} className="text-blue-700" />
      </div>
      <div className="font-display text-3xl font-bold text-slate-900 mt-2">
        {value}
      </div>
    </div>
  );
}
